'use client'

import { useEffect, useState } from 'react'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useAuth } from '@/store/hooks'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    getServicePayments,
    getServicePaymentStats,
    ServicePayment,
    ServicePaymentStats,
    PAYMENT_TYPE_LABELS,
    PAYMENT_TYPE_COLORS,
    SERVICE_PAYMENT_STATUS_LABELS,
    SERVICE_PAYMENT_STATUS_COLORS,
    getServiceTypeLabels,
} from '@/lib/api/billing/service-payment-api'
import {
    MdAttachMoney,
    MdSearch,
    MdClose,
    MdArrowBack,
    MdArrowForward,
    MdPerson
} from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function ServicePaymentsPage() {
    const serviceTypeLabels = getServiceTypeLabels(useServiceLabel())
    const { user } = useAuth()
    const navigate = useNavigate()
    const [payments, setPayments] = useState<ServicePayment[]>([])
    const [stats, setStats] = useState<ServicePaymentStats | null>(null)
    const [loading, setLoading] = useState(true)
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [typeFilter, setTypeFilter] = useState<string>('all')

    useEffect(() => {
        if (user?.companyId) {
            loadData()
        }
    }, [user?.companyId])

    const loadData = async () => {
        setLoading(true)
        try {
            const [paymentsResponse, statsResponse] = await Promise.all([
                getServicePayments(),
                getServicePaymentStats(),
            ])

            if (paymentsResponse.success) {
                setPayments(paymentsResponse.result || [])
            }
            if (statsResponse.success) {
                setStats(statsResponse.result)
            }
        } catch (error) {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoading(false)
        }
    }

    const filteredPayments = payments.filter(payment => {
        if (searchTerm) {
            const search = searchTerm.toLowerCase()
            if (
                !payment.client?.name?.toLowerCase().includes(search) &&
                !payment.healthPlan?.name?.toLowerCase().includes(search)
            ) {
                return false
            }
        }
        if (statusFilter !== 'all' && payment.status !== statusFilter) {
            return false
        }
        if (typeFilter !== 'all' && payment.paymentType !== typeFilter) {
            return false
        }
        return true
    })

    const formatCurrency = (value: number | null | undefined) => {
        const numValue = value ?? 0
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(numValue)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando pagamentos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <MdAttachMoney className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Pagamentos de Serviços
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {payments.length}{' '}
                            {payments.length === 1 ? 'pagamento' : 'pagamentos'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar pagamentos..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    setSearchTerm('')
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowSearch(true)}
                            className="btn btn-sm btn-ghost gap-2"
                        >
                            <MdSearch className="text-lg" />
                            <span className="hidden sm:inline">Buscar</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                        <p className="text-xs text-base-content/60 mb-1">
                            Total Serviços
                        </p>
                        <p className="text-xl font-bold">
                            {formatCurrency(stats.totalValue)}
                        </p>
                    </div>
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                        <p className="text-xs text-base-content/60 mb-1">
                            Convênio
                        </p>
                        <p className="text-xl font-bold text-secondary">
                            {formatCurrency(stats.totalInsurance || 0)}
                        </p>
                    </div>
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                        <p className="text-xs text-base-content/60 mb-1">
                            Particular
                        </p>
                        <p className="text-xl font-bold text-secondary">
                            {formatCurrency(stats.totalPrivate || 0)}
                        </p>
                    </div>
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                        <p className="text-xs text-base-content/60 mb-1">
                            Coparticipação
                        </p>
                        <p className="text-xl font-bold text-accent">
                            {formatCurrency(stats.totalCoparticipation || 0)}
                        </p>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
                <button
                    onClick={() => setStatusFilter('all')}
                    className={`btn btn-sm ${statusFilter === 'all' ? 'btn-secondary' : 'btn-ghost'}`}
                >
                    Todos
                </button>
                <button
                    onClick={() => setStatusFilter('PAYMENT_PENDING')}
                    className={`btn btn-sm ${statusFilter === 'PAYMENT_PENDING' ? 'btn-warning' : 'btn-ghost'}`}
                >
                    Pendentes
                </button>
                <button
                    onClick={() => setStatusFilter('PAYMENT_AUTHORIZED')}
                    className={`btn btn-sm ${statusFilter === 'PAYMENT_AUTHORIZED' ? 'btn-success' : 'btn-ghost'}`}
                >
                    Autorizados
                </button>
                <button
                    onClick={() => setStatusFilter('PAYMENT_BILLED')}
                    className={`btn btn-sm ${statusFilter === 'PAYMENT_BILLED' ? 'btn-info' : 'btn-ghost'}`}
                >
                    Faturados
                </button>
                <button
                    onClick={() => setStatusFilter('PAYMENT_COMPLETED')}
                    className={`btn btn-sm ${statusFilter === 'PAYMENT_COMPLETED' ? 'btn-success' : 'btn-ghost'}`}
                >
                    Concluidos
                </button>
            </div>

            <div className="flex gap-4 flex-wrap">
                <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="select select-bordered select-sm"
                >
                    <option value="all">Todos os Tipos</option>
                    <option value="PRIVATE">Particular</option>
                    <option value="INSURANCE">Convenio</option>
                    <option value="MIXED">Misto</option>
                </select>
            </div>

            {filteredPayments.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdAttachMoney className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhum pagamento encontrado...
                    </h3>
                    <p className="text-base-content/60">
                        Os pagamentos de serviços serão exibidos aqui
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Serviço</th>
                                <th>Tipo</th>
                                <th>Valor</th>
                                <th>Status</th>
                                <th>Data</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPayments.map(payment => (
                                <tr
                                    key={payment.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(`/billing/payments/${payment.id}`)
                                    }
                                >
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <MdPerson className="text-base-content/40" />
                                            <span>{payment.client?.name}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost badge-sm">
                                            {serviceTypeLabels[payment.serviceType] ||
                                                payment.serviceType}
                                        </span>
                                    </td>
                                    <td>
                                        {payment.paymentType ? (
                                            <span
                                                className={`badge badge-sm ${PAYMENT_TYPE_COLORS[payment.paymentType]}`}
                                            >
                                                {PAYMENT_TYPE_LABELS[payment.paymentType]}
                                            </span>
                                        ) : (
                                            <span className="text-base-content/50">
                                                -
                                            </span>
                                        )}
                                    </td>
                                    <td className="font-semibold">
                                        {formatCurrency(payment.finalValue)}
                                    </td>
                                    <td>
                                        <span
                                            className={`badge badge-sm ${SERVICE_PAYMENT_STATUS_COLORS[payment.status]}`}
                                        >
                                            {SERVICE_PAYMENT_STATUS_LABELS[payment.status]}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="text-sm">
                                            {format(
                                                new Date(payment.createdAt),
                                                'dd/MM/yyyy',
                                                { locale: ptBR }
                                            )}
                                        </span>
                                    </td>
                                    <td>
                                        <MdArrowForward className="text-base-content/40" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
